import React, { useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { useAuth, formatApiErrorDetail } from '@/context/AuthContext';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Lock, Mail } from 'lucide-react';

const LOGO_URL = '/logo.png';

const LoginPage = () => {
  const { user, login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await login(email.trim().toLowerCase(), password);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#050505] px-6 py-12">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex justify-center mb-10">
          <img
            src={LOGO_URL}
            alt="Grace The Geek Studios"
            className="h-14 w-auto object-contain"
            data-testid="login-logo"
          />
        </div>

        {/* Tagline */}
        <div className="font-mono text-xs uppercase tracking-[0.4em] text-zinc-500 text-center mb-6">
          // secure_team_access
        </div>

        {/* Terminal block */}
        <div className="terminal-block">
          {/* Title bar */}
          <div className="flex items-center gap-2 px-4 py-3 border-b border-white/[0.08]">
            <span className="terminal-dot bg-red-500/80"></span>
            <span className="terminal-dot bg-amber-400/80"></span>
            <span className="terminal-dot bg-emerald-400/80"></span>
            <span className="ml-auto font-mono text-[10px] sm:text-xs uppercase tracking-[0.35em] text-zinc-500">
              auth_login.exe
            </span>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-8 space-y-6">
            <div>
              <h1 className="font-heading text-3xl sm:text-4xl font-bold text-white leading-[1.05]">
                Initialize.
              </h1>
              <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#735AE2] leading-[1.05]">
                Session.
              </h1>
              <p className="font-mono text-xs text-zinc-500 mt-3 uppercase tracking-widest">
                <span className="text-[#735AE2]">{'>'}</span> sign in to access the dashboard
              </p>
            </div>

            <div className="space-y-2">
              <label
                htmlFor="email"
                className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 flex items-center gap-2"
              >
                <Mail className="w-3 h-3 text-[#735AE2]" />
                Email
              </label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@gracethegeekstudios.com"
                autoComplete="email"
                required
                data-testid="login-email-input"
                className="bg-white/[0.03] border-white/[0.15] text-white placeholder:text-zinc-600 font-body focus:border-[#735AE2] focus-visible:ring-0 rounded-none h-11"
              />
            </div>

            <div className="space-y-2">
              <label
                htmlFor="password"
                className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 flex items-center gap-2"
              >
                <Lock className="w-3 h-3 text-[#735AE2]" />
                Password
              </label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete="current-password"
                required
                data-testid="login-password-input"
                className="bg-white/[0.03] border-white/[0.15] text-white placeholder:text-zinc-600 font-body focus:border-[#735AE2] focus-visible:ring-0 rounded-none h-11"
              />
            </div>

            {error && (
              <div
                className="border border-rose-500/40 bg-rose-500/10 px-4 py-3 font-mono text-xs text-rose-200"
                data-testid="login-error"
              >
                <span className="text-rose-400">{'>'}</span> {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={submitting}
              data-testid="login-submit-btn"
              className="w-full bg-[#735AE2] text-white hover:bg-[#8A75EA] rounded-none px-5 py-3 font-mono text-xs uppercase tracking-[0.25em] font-medium h-12 disabled:opacity-50"
            >
              {submitting ? 'Authenticating...' : 'Sign in'}
            </Button>

            <div className="pt-4 border-t border-white/[0.06] font-mono text-[10px] uppercase tracking-widest text-zinc-500 flex justify-between">
              <span>// invite-only access</span>
              <Link to="/register" className="text-[#735AE2] hover:text-[#9D8AEC]" data-testid="register-link">
                register {'>'}
              </Link>
            </div>
          </form>
        </div>

        {/* Footer note */}
        <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-zinc-600 text-center mt-8">
          // GRACE THE GEEK STUDIOS · {new Date().getFullYear()}
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
